import { useCallback, useState } from "react";
import { includes } from "lodash";

import { defaultErrorMessage } from "lib/axios/constants";
import { ErrorInfo, ErrorType } from "services/royaltyAccount/types";
import {
  createValidateRoyaltyAccountCredentialsTask,
  getValidateRoyaltyAccountCredentialsTask,
  updateValidateRoyaltyAccountCredentialsTask,
} from "services/validateRoyaltyAccountCredentialsTasks";
import {
  CreateValidateRoyaltyAccountCredentialsTaskPayload,
  ValidateRoyaltyAccountCredentialsTask,
} from "services/validateRoyaltyAccountCredentialsTasks/types";

const twoFARequiredMessage = "Two factor authentication required";
const twoFAInvalidMessage =
  "Invalid two factor authentication code was submitted";
const twoFAInProgressMessages = [twoFARequiredMessage, twoFAInvalidMessage];

const unknownScraperErrorMessage = "Unknown scraper error";
const TASK_END_STATES = ["completed", "failed"];

let intervalId = 0;
const cancelRunningTasks = () => {
  if (intervalId !== 0) {
    window.clearInterval(intervalId);
  }
};

const validateRoyaltyAccountCredentials = async (
  values: CreateValidateRoyaltyAccountCredentialsTaskPayload,
): Promise<ValidateRoyaltyAccountCredentialsTask> => {
  cancelRunningTasks();

  const task = await createValidateRoyaltyAccountCredentialsTask({
    ...values,
  });

  return await new Promise<ValidateRoyaltyAccountCredentialsTask>(resolve => {
    intervalId = window.setInterval(() => pollTask(task.id, resolve), 2000);
  });
};

export const continueValidateRoyaltyAccountCredentials = async (
  taskId: number,
  values: Partial<CreateValidateRoyaltyAccountCredentialsTaskPayload>,
): Promise<ValidateRoyaltyAccountCredentialsTask> => {
  cancelRunningTasks();
  const task = await updateValidateRoyaltyAccountCredentialsTask(
    taskId,
    values,
  );
  return await new Promise<ValidateRoyaltyAccountCredentialsTask>(resolve => {
    intervalId = window.setInterval(() => pollTask(task.id, resolve), 2000);
  });
};

const pollTask = async (taskId: number, resolve: any) => {
  try {
    const task = await getValidateRoyaltyAccountCredentialsTask(taskId);
    if (
      includes(TASK_END_STATES, task.state) ||
      twoFAInProgressMessages.includes(task.status_message)
    ) {
      cancelRunningTasks();
      resolve(task);
    }
  } catch {
    cancelRunningTasks();
    resolve({ error: defaultErrorMessage });
  }
};

const encodeAccountValidationError = (error: string): ErrorInfo => {
  let errorType: ErrorType;
  switch (error) {
    // messages must be in sync with "orderbook/scrapers/messages"
    case "Account has been locked":
      errorType = "accountLocked";
      break;
    case "Invalid username/password":
    case "User does not exist":
      errorType = "invalidProLogin";
      break;
    case "Verify email address":
      errorType = "email";
      break;
    case "Password expired":
      errorType = "passwordExpired";
      break;
    case "Password needs update":
      errorType = "passwordNeedsUpdate";
      break;
    case "No statements found on account":
    case "No statements found!":
      errorType = "noStatements";
      break;
    case "Portal has changed":
      errorType = "portalChanged";
      break;
    case "Invalid two factor authentication code was submitted":
      errorType = "invalid2FASubmitted";
      break;
  }
  if (!errorType) {
    if (error) {
      errorType = "other";
    } else {
      errorType = "connection";
    }
  }

  return {
    date: new Date(),
    errorType,
    message: error,
  };
};

interface UseValidateRoyaltyAccountCredentialsResult {
  isOnTwoFAStep: boolean;
  isValidatingCredentials: boolean;
  validateCredentials: (
    credentials: CreateValidateRoyaltyAccountCredentialsTaskPayload,
  ) => Promise<boolean>;
  validateCredentialsErrorInfo: ErrorInfo | null;
  validationResult: Record<string, string> | null;
}

export const useValidateRoyaltyAccountCredentials = (): UseValidateRoyaltyAccountCredentialsResult => {
  const [errorInfo, setErrorInfo] = useState<ErrorInfo | null>(null);
  const [isValidatingCredentials, setIsValidatingCredentials] = useState(false);
  const [
    currentTask,
    setCurrentTask,
  ] = useState<ValidateRoyaltyAccountCredentialsTask | null>(null);
  const [isOnTwoFAStep, setIsOnTwoFAStep] = useState(false);
  const [validationResult, setValidationResult] = useState<Record<
    string,
    string
  > | null>(null);

  const validateCredentials = useCallback(
    async (
      credentials: CreateValidateRoyaltyAccountCredentialsTaskPayload,
    ): Promise<boolean> => {
      setErrorInfo(null);
      setIsValidatingCredentials(true);
      if (!credentials.two_fa_code) {
        setIsOnTwoFAStep(false);
      }
      setValidationResult(null);

      let success = false;
      try {
        const task = !currentTask
          ? await validateRoyaltyAccountCredentials(credentials)
          : await continueValidateRoyaltyAccountCredentials(
              currentTask.id,
              credentials.two_fa_code
                ? { two_fa_code: credentials.two_fa_code }
                : {},
            );

        setCurrentTask(task.state === "ready" ? task : null);
        const error =
          task.state === "failed" ||
          (task.state === "completed" && Number(task.status_progress) < 1)
            ? !!task.status_message
              ? task.status_message
              : !!task.status_notes
              ? task.status_notes
              : unknownScraperErrorMessage
            : undefined;

        if (twoFAInProgressMessages.includes(task.status_message)) {
          setIsOnTwoFAStep(true);
          if (task.status_message !== twoFARequiredMessage) {
            setErrorInfo(encodeAccountValidationError(task.status_message));
          }
          success = false;
        } else if (!error) {
          setValidationResult(task.subaccounts);
          setIsOnTwoFAStep(false);
          setErrorInfo(null);
          setIsValidatingCredentials(false);
          success = true;
        } else {
          setErrorInfo(encodeAccountValidationError(task.status_message));
          setIsOnTwoFAStep(false);
          success = false;
        }
      } catch (e) {
        const errorMessage = typeof e === "string" ? e : defaultErrorMessage;
        setErrorInfo(encodeAccountValidationError(errorMessage));
        setIsOnTwoFAStep(false);
      } finally {
        setIsValidatingCredentials(false);
        return success;
      }
    },
    [currentTask],
  );

  return {
    isOnTwoFAStep,
    isValidatingCredentials,
    validateCredentials,
    validateCredentialsErrorInfo: errorInfo,
    validationResult,
  };
};
